import React, { useCallback, useMemo, useRef, useState } from "react";
import Papa from "papaparse";
import * as yup from "yup";
import {
  Button,
  Card,
  Container,
  Form,
  Row,
  Col,
  Table,
  Alert,
  Spinner,
  Accordion,
} from "react-bootstrap";

const makeRowSchema = (canUseCustomVlan) =>
  yup
    .object({
      email: yup
        .string()
        .trim()
        .email("Invalid email")
        .matches(
          /^[a-zA-Z0-9-_.]+@[a-zA-Z0-9-]+([.-][a-zA-Z0-9]+)*.[a-zA-Z]{2,}$/,
          "Email should be a valid email address"
        )
        .required("email is required"),
      firstname: yup.string().trim().required("firstname is required"),
      lastname: yup.string().trim().required("lastname is required"),
      customvlan: yup
        .number()
        .min(0)
        .when("userlevel", {
          is: 2,
          then: (schema) =>
            schema.required("customvlan is required for a Custom user level"),
          otherwise: (schema) => schema.strip(),
        }),
      userlevel: yup
        .number()
        .oneOf(canUseCustomVlan ? [0, 1, 2] : [0, 1])
        .transform((value, originalValue) => {
          if (
            typeof originalValue === "string" &&
            originalValue.trim() !== ""
          ) {
            const n = Number(originalValue);
            return Number.isFinite(n) ? n : NaN;
          }
          return value;
        })
        .typeError("userlevel must be a number")
        .required("userlevel is required"),
    })
    .required();

const REQUIRED_HEADERS = ["email", "firstname", "lastname", "userlevel"];
const VLAN_HEADERS = [...REQUIRED_HEADERS, "customvlan"];

export default function MultipleUserCsvInput({
  onRowsChange,
  maxUsers = 0,
  canUseCustomVlan = false,
}) {
  const [validRows, setValidRows] = useState([]);
  const [invalidRows, setInvalidRows] = useState([]);
  const [errors, setErrors] = useState([]);
  const [headerIssues, setHeaderIssues] = useState([]);
  const [fileName, setFileName] = useState("");
  const [parsing, setParsing] = useState(false);
  const inputRef = useRef(null);
  const rowSchema = useMemo(
    () => makeRowSchema(canUseCustomVlan),
    [canUseCustomVlan]
  );

  const handleClear = useCallback(() => {
    setValidRows([]);
    setInvalidRows([]);
    setErrors([]);
    setHeaderIssues([]);
    setFileName("");
    setParsing(false);
    if (inputRef.current) inputRef.current.value = "";
  }, []);

  const validateHeaders = useCallback((headers) => {
    const normalized = headers.map((h) => h?.toString().trim().toLowerCase());
    const missing = REQUIRED_HEADERS.filter(
      (need) => !normalized.includes(need)
    );
    const extras = normalized.filter((h) => h && !REQUIRED_HEADERS.includes(h));
    return { missing, extras, normalized };
  }, []);

  const parseCsv = useCallback(
    (file) => {
      setParsing(true);
      setValidRows([]);
      setInvalidRows([]);
      setErrors([]);
      setHeaderIssues([]);

      Papa.parse(file, {
        header: true,
        skipEmptyLines: "greedy",
        transformHeader: (header) => header.trim().toLowerCase(),
        transform: (value) =>
          typeof value === "string" ? value.trim() : value,
        complete: async (results) => {
          try {
            const { data, meta, errors: papaErrors } = results;

            const initialIssues = papaErrors?.length
              ? papaErrors.map((e) => ({
                  row: e.row ?? null,
                  messages: [
                    `Parser: ${e.code}${e.type ? ` (${e.type})` : ""}${
                      e.message ? ` - ${e.message}` : ""
                    }`,
                  ],
                }))
              : [];

            const { missing, extras } = validateHeaders(meta?.fields || []);
            const headerProblems = [];
            if (missing.length)
              headerProblems.push(
                `Missing required headers: ${missing.join(", ")}`
              );
            // if (extras.length)
            //   headerProblems.push(`Found extra headers: ${extras.join(", ")}`);
            setHeaderIssues(headerProblems);

            if (missing.length) {
              setParsing(false);
              setValidRows([]);
              setInvalidRows([]);
              setErrors([...initialIssues]);
              return;
            }

            const validated = [];
            const invalidated = [];
            const rowErrors = [...initialIssues];
            const seenEmails = new Set();

            for (let i = 0; i < data.length; i++) {
              const rawRow = data[i];
              const row = Object.fromEntries(
                VLAN_HEADERS.map((h) => [h, rawRow[h]])
              );

              try {
                const clean = await rowSchema.validate(row, {
                  abortEarly: false,
                  stripUnknown: true,
                });

                if (seenEmails.has(clean.email.toLowerCase())) {
                  rowErrors.push({
                    row: i + 2,
                    messages: ["Duplicate email detected"],
                  });
                  invalidated.push({ ...row, __rownum: i + 2 });
                } else if (maxUsers && validated.length >= maxUsers) {
                  rowErrors.push({
                    row: i + 2,
                    messages: ["Not enough user slots for this user"],
                  });
                  invalidated.push({ ...row, __rownum: i + 2 });
                } else {
                  seenEmails.add(clean.email.toLowerCase());
                  validated.push(clean);
                }
              } catch (err) {
                if (err?.inner?.length) {
                  rowErrors.push({
                    row: i + 2,
                    messages: Array.from(
                      new Set(err.inner.map((e) => e.message))
                    ),
                  });
                } else {
                  rowErrors.push({
                    row: i + 2,
                    messages: [err.message || "Validation error"],
                  });
                }
                invalidated.push({ ...row, __rownum: i + 2 });
              }
            }

            setValidRows(validated);
            if (onRowsChange) {
              onRowsChange(validated, invalidated);
            }
            setInvalidRows(invalidated);
            setErrors(rowErrors);
          } finally {
            setParsing(false);
          }
        },
        error: (error) => {
          setParsing(false);
          setErrors([
            { row: null, messages: [error?.message || "Failed to parse file"] },
          ]);
        },
      });
    },
    [maxUsers, onRowsChange, validateHeaders, rowSchema]
  );

  const handleFile = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setFileName(file.name);
      parseCsv(file);
    },
    [parseCsv]
  );

  const downloadTemplate = useCallback(() => {
    const headerRow = REQUIRED_HEADERS.join(",");
    const csv = `${headerRow}\n`;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "users_template.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  const summary = useMemo(() => {
    const valid = validRows.length;
    const invalid = errors.length;
    return { valid, invalid };
  }, [validRows.length, errors.length]);

  return (
    <Container className="py-4">
      <Row className="mb-3">
        <Col>
          <h2>CSV User Import</h2>
          <p>
            Upload a CSV with headers: email, firstname, lastname, userlevel.
            Duplicate emails are not allowed. Additional headers will be
            ignored. Any invalid rows will not be submitted.
          </p>
          {canUseCustomVlan && (
            <p>
              You are also able to add a custom vlan using the customvlan
              header.
            </p>
          )}
          <p>For User Level:</p>
          <ul>
            {canUseCustomVlan && <li key={2}>2 - Custom</li>}
            <li key={1}>1 - Web & Lan (Recommended)</li>
            <li key={0}>
              0 - Web Only (Not Recommended, legacy and may break connectivity)
            </li>
          </ul>
        </Col>
      </Row>

      <Row>
        <Col className="d-flex justify-content-end gap-2 mb-2">
          <Button variant="secondary" onClick={downloadTemplate}>
            Download Blank CSV
          </Button>
          <Button variant="outline-secondary" onClick={handleClear}>
            Clear
          </Button>
        </Col>
      </Row>

      <Row>
        <Card className="mb-3">
          <Card.Body>
            <Form.Group controlId="csv-input" className="mb-2">
              <Form.Label>Select CSV File</Form.Label>
              <Form.Control
                ref={inputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={handleFile}
              />
              {fileName && <Form.Text>Selected: {fileName}</Form.Text>}
            </Form.Group>
            {parsing && (
              <Spinner animation="border" size="sm" className="ms-2" />
            )}
            {headerIssues.map((issue, i) => (
              <Alert key={i} variant="warning" className="p-2">
                {issue}
              </Alert>
            ))}
          </Card.Body>
        </Card>
      </Row>

      <Row>
        <Card className="mb-3">
          <Card.Header>Valid Rows ({summary.valid})</Card.Header>
          <Card.Body style={{ maxHeight: "300px", overflow: "auto" }}>
            {validRows.length ? (
              <Table striped bordered size="sm">
                <thead>
                  <tr>
                    {canUseCustomVlan
                      ? VLAN_HEADERS.map((h) => <th key={h}>{h}</th>)
                      : REQUIRED_HEADERS.map((h) => <th key={h}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {validRows.map((row, i) => (
                    <tr key={i}>
                      <td>{row.email}</td>
                      <td>{row.firstname}</td>
                      <td>{row.lastname}</td>
                      <td>{row.userlevel}</td>
                      {canUseCustomVlan && (
                        <td>
                          {Number(row.userlevel) === 2
                            ? row.customvlan ?? "Unset"
                            : "N/A"}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <p>No valid rows yet.</p>
            )}
          </Card.Body>
        </Card>

        <Card className="mb-3">
          <Card.Header>Invalid Rows ({invalidRows.length})</Card.Header>
          <Card.Body style={{ maxHeight: "300px", overflow: "auto" }}>
            {invalidRows.length ? (
              <Table striped bordered size="sm">
                <thead>
                  <tr>
                    {canUseCustomVlan
                      ? VLAN_HEADERS.map((h) => <th key={h}>{h}</th>)
                      : REQUIRED_HEADERS.map((h) => <th key={h}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {invalidRows.map((row, i) => (
                    <tr key={i}>
                      <td>{row.__rownum}</td>
                      <td>{row.email}</td>
                      <td>{row.firstname}</td>
                      <td>{row.lastname}</td>
                      <td>{row.userlevel}</td>
                      {canUseCustomVlan && (
                        <td>
                          {Number(row.userlevel) === 2
                            ? row.customvlan ?? "Unset"
                            : "N/A"}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <p>No invalid rows.</p>
            )}
          </Card.Body>
        </Card>
      </Row>

      <Row>
        <Card className="mb-3">
          <Card.Header>Errors ({summary.invalid})</Card.Header>
          <Card.Body
            style={{
              maxHeight: "300px",
              overflow: "auto",
              flex: false,
            }}
          >
            {errors.length ? (
              errors.map((err, index) => (
                <Alert
                  key={index}
                  variant="danger"
                  className="align-items-center p-2 m-0 my-1"
                >
                  <strong>Row {err.row ?? "?"}</strong>
                  <ul style={{ margin: 0 }}>
                    {err.messages.map((message, messageIndex) => (
                      <li key={messageIndex}>{message}</li>
                    ))}
                  </ul>
                </Alert>
              ))
            ) : (
              <p>No errors.</p>
            )}
          </Card.Body>
        </Card>
      </Row>

      {validRows.length > 0 && (
        <Accordion className="mb-3">
          <Accordion.Item eventKey="0">
            <Accordion.Header>JSON Output</Accordion.Header>
            <Accordion.Body>
              <pre style={{ maxHeight: "300px", overflow: "auto" }}>
                {JSON.stringify(validRows, null, 2)}
              </pre>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      )}
    </Container>
  );
}
